import { useState, useEffect, useMemo, useRef } from 'react'
import { fetchGolfPairings, fetchGolfLiveScores, fetchGolfRoundScores } from '../lib/odds'
import { saveGolfPicks, loadGolfPicks } from '../lib/db'
import {
  allRoundWindows, isPairingLocked, parseTeeTimeMs,
  isRoundInProgress, shouldShowLiveScores,
} from '../lib/golfPickWindow'
import { flagUrl, playerInitials } from '../lib/golfCountryFlag'
import { formatScoreToPar } from '../lib/golfDisplay'
import { log } from '../lib/log'

// Live in-progress scores: 75s poll cadence, 3 consecutive failures →
// drop the scores rather than show stale numbers as fresh.
const LIVE_POLL_INTERVAL_MS    = 75 * 1000
const LIVE_POLL_MAX_FAILURES   = 3

// Golf pick'em mode — matchups variant (2-ball/3-ball, picker selects
// winner of each pairing). Single source of truth for pickability is
// src/lib/golfPickWindow.js (allRoundWindows / isPairingLocked) — the
// same module the server-side grader's assumptions align with (no
// independent lock math in the UI).
//
// Four-window lifecycle, legible at a glance:
//   • 'not-published'  — pairings not in feed yet (R3-R4 pre-cut)
//   • 'open'           — all teeTimes future; picking allowed
//   • 'partial-locked' — some pairings locked by tee time, others open
//   • 'fully-locked'   — all teeTimes past; round closed
//
// Init: fetch pairings for ALL pool rounds in parallel + load saved
// picks per round. Round-tab badges reflect state + pick progress
// without requiring the user to navigate to each round first.
//
// Clock: ticked every 30s to keep per-pairing lock state fresh as tee
// times pass during a round. Re-deriving via useMemo (cheap; one pure
// function call against pairings+now+tzOffset).
//
// PER-PAIRING LOCKS — driven by isPairingLocked(teeTime, now,
// tzOffsetHours). A pick can be made iff the pairing's teeTime is in
// the future. The submit button is gated by round-level windowState
// (any-pickable + has-picks + not-submitted).

const CLOCK_TICK_MS = 30 * 1000

const STATE_LABEL = {
  'not-published':   'Pairings TBA',
  'open':            'Open',
  'partial-locked':  'Locks rolling',
  'fully-locked':    'Locked',
}

const STATE_COLOR = {
  'not-published':   { fg: 'var(--text-muted)', bg: 'var(--surface2)', border: 'var(--border)' },
  'open':            { fg: 'var(--green)',      bg: 'var(--surface2)', border: 'var(--green)'  },
  'partial-locked':  { fg: 'var(--gold)',       bg: 'var(--gold-bg2)', border: 'var(--gold)'   },
  'fully-locked':    { fg: 'var(--text-muted)', bg: 'var(--surface2)', border: 'var(--border)' },
}

// Helper: format DataGolf "YYYY-MM-DD HH:MM" → "10:25 AM" tournament-local
function formatTeeTime(teeTime) {
  if (!teeTime) return ''
  const m = String(teeTime).match(/^\d{4}-\d{2}-\d{2} (\d{2}):(\d{2})$/)
  if (!m) return teeTime
  let h = parseInt(m[1], 10)
  const min = m[2]
  const am = h < 12
  if (h === 0) h = 12
  else if (h > 12) h -= 12
  return `${h}:${min} ${am ? 'AM' : 'PM'}`
}

// DataGolf serves names as "Last, First". Display as "First Last".
function displayName(name) {
  if (!name) return ''
  const s = String(name).trim()
  if (!s.includes(',')) return s
  const [last, first] = s.split(',').map(t => t.trim())
  return first ? `${first} ${last}` : s
}

// formatScoreToPar moved to src/lib/golfDisplay.js — shared with the
// divisions sheet + leaderboard so the four golf surfaces (matchup
// picker, divisions sheet, standings podium, golf MemberResultsSheet)
// can't drift on sign/E/null conventions. Imported above.

// Display-only lock countdown — derived from the SAME parseTeeTimeMs
// the lock check uses, so display can never drift from lock state.
// Returns "Locks in 2h 15m" / "Locks in 45m" / "Locks in <1m"; returns
// null when the pairing's tee time has already passed (caller renders
// the locked badge instead).
function formatLockCountdown(teeTime, nowMs, tzOffsetHours) {
  const t = parseTeeTimeMs(teeTime, tzOffsetHours)
  if (t == null || nowMs >= t) return null
  const totalSec = Math.floor((t - nowMs) / 1000)
  const days  = Math.floor(totalSec / 86400)
  const hours = Math.floor((totalSec % 86400) / 3600)
  const mins  = Math.floor((totalSec % 3600) / 60)
  if (days  > 0) return `Locks in ${days}d ${hours}h`
  if (hours > 0) return `Locks in ${hours}h ${mins}m`
  if (mins  > 0) return `Locks in ${mins}m`
  return 'Locks in <1m'
}

export default function GolfPicksMode({
  group, user, members, onPointsUpdate, gradeKey = 0, promptAuth = () => {},
  // Pattern #2 freemium tease (golf-picks prototype). When `locked` is
  // true, taps still update local pick state (the user SEES the
  // selection — the tease) but the per-tap saveGolfPicks call is
  // SKIPPED (no server INSERT, no localStorage write — picks are
  // ephemeral). handleSubmit gates AFTER the existing
  // selectedRound/arr.length validation so locked-state behavior
  // matches the unlocked flow's "pick at least one pairing first"
  // toast (consistent UX guidance — user can't unlock without picks
  // already in place). The existing sticky-submit at bottom:80/z:90 is
  // suppressed when locked; the sticky unlock bar replaces it as the
  // single bottom CTA at the same position. Toast stays at z:100
  // (above). 041's RESTRICTIVE policies on golf_picks remain the
  // source of truth. When `locked` is false (default), behavior is
  // byte-identical to pre-Pattern-#2.
  locked = false,
  onUnlockRequest,
  unlocking = false,
  unlockPriceCents = null,
}) {
  const eventId    = group.golf_config?.eventId
  const poolRounds = Array.isArray(group.golf_config?.rounds) ? group.golf_config.rounds : []
  const poolRoundsKey = poolRounds.join('|')

  // Pairings + tz, loaded once at init. pairingsByRound is keyed by
  // round number → array. tzOffsetHours comes from the API response
  // (event-level, same for every round).
  const [pairingsByRound, setPairingsByRound] = useState({})
  const [tzOffsetHours,   setTzOffsetHours]   = useState(0)

  // Per-round picks and submitted flag. Identical shape to the prior
  // round-status version — saveGolfPicks / loadGolfPicks unchanged.
  const [picks,     setPicks]     = useState({})  // {<round>: {<groupId>: pickObj}}
  const [submitted, setSubmitted] = useState({})

  const [selectedRound, setSelectedRound] = useState(null)
  const [loading,       setLoading]       = useState(true)
  const [toast,         setToast]         = useState(null)
  const toastTimerRef = useRef(null)

  // Clock for re-deriving lock state. Updated every 30s so per-pairing
  // locked badges flip as tee times pass during a round.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS)
    return () => clearInterval(id)
  }, [])

  function showToast(msg) {
    setToast(msg)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(null), 2000)
  }

  // ── Init: parallel fetch of pairings + saved picks for ALL pool rounds.
  //    The "at-a-glance" lifecycle visibility requires data for every round
  //    upfront — otherwise round-tab badges can't show pick counts until
  //    each round has been navigated to.
  useEffect(() => {
    if (!eventId || !poolRounds.length) {
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const results = await Promise.all(poolRounds.map(async r => {
        const [bundle, saved] = await Promise.all([
          fetchGolfPairings(eventId, r),
          loadGolfPicks(group.id, user.id, r),
        ])
        return { round: r, bundle, saved }
      }))
      if (cancelled) return

      const nextPairings = {}
      const nextPicks    = {}
      const nextSubmit   = {}
      let tz = 0
      for (const { round, bundle, saved } of results) {
        nextPairings[round] = bundle?.pairings || []
        if (typeof bundle?.tzOffsetHours === 'number') tz = bundle.tzOffsetHours
        if (saved) {
          nextPicks[round]  = saved.picks || {}
          nextSubmit[round] = !!saved.submitted
        }
      }
      setPairingsByRound(nextPairings)
      setTzOffsetHours(tz)
      setPicks(nextPicks)
      setSubmitted(nextSubmit)

      // Default-select the first round that still has any pickable pairing
      // (windowState 'open' or 'partial-locked'). Fallback to first pool
      // round so a fully-locked tournament still lands somewhere.
      const windows = allRoundWindows({ pairingsByRound: nextPairings, now: Date.now(), tzOffsetHours: tz })
      const firstPickable = poolRounds.find(r => {
        const w = windows.find(x => x.round === r)
        return w && (w.state === 'open' || w.state === 'partial-locked')
      })
      setSelectedRound(firstPickable ?? poolRounds[0])
      setLoading(false)
    })()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, poolRoundsKey, group.id, user.id, gradeKey])

  // Derived: window state per round (for the round-tab badge strip + the
  // round-level disable logic). useMemo so the ticking clock doesn't
  // re-run windowState on every render between ticks.
  const roundWindows = useMemo(
    () => allRoundWindows({ pairingsByRound, now, tzOffsetHours }),
    [pairingsByRound, now, tzOffsetHours]
  )

  // ── Live in-progress scores (display-only) ──────────────────────────────
  // Polls /api/golf/live-scores for the selected round, but ONLY when the
  // round is in progress (some pairing locked AND no picks graded yet).
  // The map shape mirrors the GolfRoundScore typedef minus name:
  //   { [dgId]: { score, thru, status } }
  // Render layer uses shouldShowLiveScores per pairing to enforce the
  // leakage gate (unlocked pairings never show live opponent scores).
  const [liveScoresByDgId, setLiveScoresByDgId] = useState({})

  // ── Per-golfer per-round scores (display-only) ───────────────────────────
  // Drives the R1-R4 strip in the picker (line ~855 site). Polled at the
  // same 75s cadence as live-scores; the server-side /api/golf/round-scores
  // endpoint does 4 sequential getRoundScores calls per request, each
  // riding the 45s live-stats cache. Steady-state per poll: 1 cold call
  // (the current round) + 3 cache hits ≈ live-scores latency × 1.5.
  //
  // Shape: { scoresByDg: { <dgId>: { 1?: score|null, 2?: ..., 3?: ..., 4?: ... } },
  //          rounds:     { 1: 'final'|'in_progress'|'no_data', ... } }
  //
  // Falls back to {} on fetch error — strip renders dash naturally. Cleared
  // when eventId is null (no event configured).
  const [roundScoresAll, setRoundScoresAll] = useState({ scoresByDg: {}, rounds: {} })

  // Derived: is the selected round in progress? Drives polling + render.
  // Pure predicate from golfPickWindow.js — same module that's already
  // the source of truth for round-state. See pickWindow.test.js for the
  // full truth table.
  const currentRoundWindow = useMemo(
    () => roundWindows.find(w => w.round === selectedRound),
    [roundWindows, selectedRound]
  )
  const currentRoundPicks  = useMemo(
    () => Object.values(picks[selectedRound] || {}),
    [picks, selectedRound]
  )
  const isInProgress = useMemo(
    () => isRoundInProgress({
      roundState: currentRoundWindow?.state ?? 'not-published',
      roundPicks: currentRoundPicks,
    }),
    [currentRoundWindow, currentRoundPicks]
  )

  useEffect(() => {
    if (!isInProgress || !eventId || selectedRound == null) {
      // Not in progress (round graded, future, or pre-tee) → clear stale
      // live data so the render falls back to finals (pick.roundScores)
      // or "—" naturally.
      setLiveScoresByDgId({})
      return
    }
    let cancelled    = false
    let timer        = null
    let failureCount = 0

    async function tick() {
      if (document.hidden) return   // tab in background — skip the poll
      const result = await fetchGolfLiveScores(eventId, selectedRound)
      if (cancelled) return
      if (result.fetchError) {
        failureCount++
        if (failureCount >= LIVE_POLL_MAX_FAILURES) {
          // Persistent failure — drop scores rather than show stale data
          // as current. Same posture as the third-failure → empty
          // fallback in fetchGolfPairings (the lesson from the
          // "Pairings TBA" silent-empty incident).
          setLiveScoresByDgId({})
        }
        return
      }
      failureCount = 0
      const map = {}
      for (const s of result.scores) {
        if (!s?.dgId) continue
        map[String(s.dgId)] = { score: s.score, thru: s.thru, status: s.status }
      }
      setLiveScoresByDgId(map)
    }

    tick()   // immediate first poll
    timer = setInterval(tick, LIVE_POLL_INTERVAL_MS)

    function onVisChange() {
      if (!document.hidden) tick()   // re-poll on tab focus return
    }
    document.addEventListener('visibilitychange', onVisChange)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisChange)
    }
  }, [isInProgress, eventId, selectedRound])

  // ── R1-R4 strip polling (display-only) ──────────────────────────────────
  // Polls /api/golf/round-scores at the same 75s cadence as live-scores.
  // Unlike live-scores this runs WHENEVER the picker is mounted — finished
  // rounds need data too for the strip (live-styled rounds + authoritative
  // finals from non-picked golfers). The server-side adapter cache (45s)
  // bounds the cost; steady-state per poll is ~1 cold + 3 cache hits.
  //
  // Pauses on document.hidden, re-polls on visibility return — same pattern
  // as live-scores. Failure handling: empty bundle on error → strip falls
  // back to dash treatment via the cellFor logic at render time.
  useEffect(() => {
    if (!eventId) {
      setRoundScoresAll({ scoresByDg: {}, rounds: {} })
      return
    }
    let cancelled = false
    let timer     = null

    async function tick() {
      if (document.hidden) return
      const result = await fetchGolfRoundScores(eventId)
      if (cancelled) return
      if (result.fetchError) return  // keep last good data
      setRoundScoresAll({
        scoresByDg: result.scoresByDg,
        rounds:     result.rounds,
      })
    }

    tick()
    timer = setInterval(tick, LIVE_POLL_INTERVAL_MS)

    function onVisChange() {
      if (!document.hidden) tick()
    }
    document.addEventListener('visibilitychange', onVisChange)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisChange)
    }
  }, [eventId])

  // Per-round per-dgId snapshot lookup from the user's own picks. Walks
  // every round's picks and builds:
  //   { 1: { <dgId>: score | null }, 2: ..., 3: ..., 4: ... }
  // A dgId-key with value null means the grader saw the golfer as path-b
  // absent or WD for that round (snapshot frozen at finalize). A missing
  // key means the golfer wasn't in the user's pairing for that round —
  // strip falls back to the live endpoint's data for those cells.
  //
  // This is the "snapshot precedence" half of the YES branch from Part 2
  // of the round-score-strip relay: for any golfer who appears in the
  // user's grouping_members for a round, we display the GRADER's frozen
  // score (which is also what their pool points were computed from). For
  // any other golfer, we use the live endpoint's data (DataGolf may have
  // revised, but the user wasn't graded on that golfer so consistency
  // doesn't apply).
  const pickScoresByRoundByDgId = useMemo(() => {
    const map = { 1: {}, 2: {}, 3: {}, 4: {} }
    for (const r of [1, 2, 3, 4]) {
      const roundPicks = picks[r] || {}
      for (const pick of Object.values(roundPicks)) {
        const rs = pick?.roundScores
        if (rs && typeof rs === 'object') {
          for (const [dgId, score] of Object.entries(rs)) {
            map[r][String(dgId)] = score
          }
        }
      }
    }
    return map
  }, [picks])

  // ── Pick handler ────────────────────────────────────────────────────────
  // Locks are PER PAIRING. The handler checks the specific pairing's
  // teeTime before allowing the pick — round-level state isn't enough
  // (a round can be partial-locked with some pairings still pickable).
  async function handlePick(grouping, golfer) {
    if (selectedRound == null) return
    // Anonymous-browsing gate — each tap fires saveGolfPicks; pop the
    // Auth overlay and stop if logged out. No local state update either,
    // so a logged-out user doesn't see a phantom pick post-sign-in.
    if (!user?.id) return promptAuth()
    // NOTE: no submitted-gate here. Submit is an affirmation, not a
    // freeze — still-open pairings stay pickable after submit.
    // Per-pairing lock by tee time is the only gate.
    if (isPairingLocked(grouping.teeTime, now, tzOffsetHours)) return

    const newPick = {
      groupIdGolf:     grouping.groupId,
      selectedDgId:    golfer.dgId,
      selectedName:    golfer.name,
      groupingMembers: Array.isArray(grouping.golfers) ? grouping.golfers : [],
    }
    const newRoundPicks = { ...(picks[selectedRound] || {}), [grouping.groupId]: newPick }
    setPicks(prev => ({ ...prev, [selectedRound]: newRoundPicks }))

    // Pattern #2 intercept — locked pool: skip the auto-save entirely.
    // Local pick state still updates above (the user sees their
    // selection), but no server INSERT and no localStorage write
    // happens. Picks are ephemeral and will be lost on unlock or page
    // reload. 041's RESTRICTIVE policy on golf_picks would 42501 the
    // INSERT anyway.
    if (locked) return

    try {
      await saveGolfPicks(
        group.id, user.id, eventId, selectedRound,
        Object.values(newRoundPicks),
        !!submitted[selectedRound],
      )
    } catch (err) {
      log('[golf-picks] auto-save failed:', err?.message)
    }
  }

  async function handleSubmit() {
    if (selectedRound == null) return
    // Anonymous-browsing gate — saveGolfPicks needs user.id.
    if (!user?.id) return promptAuth()
    const roundPicks = picks[selectedRound] || {}
    const arr = Object.values(roundPicks)
    if (!arr.length) { showToast('Pick at least one pairing first'); return }
    // Pattern #2 intercept — locked pool: route to unlock checkout
    // instead of writing. Placed AFTER the selectedRound/arr.length
    // validation so the user gets the "pick at least one pairing first"
    // toast when they have no picks (consistent UX guidance — they need
    // picks before unlocking is meaningful). 041's RESTRICTIVE policy
    // on golf_picks would 42501 the INSERT anyway.
    if (locked) {
      onUnlockRequest?.()
      return
    }

    setSubmitted(prev => ({ ...prev, [selectedRound]: true }))
    try {
      await saveGolfPicks(group.id, user.id, eventId, selectedRound, arr, true)
      showToast(`Round ${selectedRound} submitted`)
      onPointsUpdate?.()
    } catch (err) {
      log('[golf-picks] submit failed:', err?.message)
      setSubmitted(prev => ({ ...prev, [selectedRound]: false }))
      showToast('Submit failed — retry')
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────

  if (!eventId || !poolRounds.length) return (
    <div style={{ padding:'40px 20px', textAlign:'center', color:'var(--text-dim)' }}>
      <div style={{ fontSize:'40px', marginBottom:'12px' }}>⛳</div>
      <div style={{ fontSize:'14px', marginBottom:'8px' }}>This golf pool is missing its configuration.</div>
      <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>
        golf_config.eventId or rounds not set on this pool.
      </div>
    </div>
  )

  if (loading) return (
    <div style={{ textAlign:'center', padding:'40px', color:'var(--text-dim)' }}>
      Loading…
    </div>
  )

  const currentWindow    = roundWindows.find(w => w.round === selectedRound) || { state: 'not-published' }
  const currentPairings  = pairingsByRound[selectedRound] || []
  const currentPicks     = picks[selectedRound] || {}
  const isSubmitted      = !!submitted[selectedRound]
  const totalPairings    = currentPairings.length
  const pickedCount      = Object.keys(currentPicks).length
  // "Can submit" = at least one pick AND not already submitted AND
  // some pairing is still open (no point submitting a fully-locked
  // round — the picks are already saved).
  const canSubmit        = !isSubmitted
                            && pickedCount > 0
                            && (currentWindow.state === 'open' || currentWindow.state === 'partial-locked')

  // Stage 7 — closed-round summary. Denominator is totalPairings, not
  // pickedCount; a no-pick pairing counts toward "not correct" since
  // the grader's worldview treats missing as lost.
  let correctCount = 0
  let pointsTotal  = 0
  for (const p of Object.values(currentPicks)) {
    if (p?.result === 'correct') correctCount++
    pointsTotal += p?.pointsEarned || 0
  }
  const isFullyLocked = currentWindow.state === 'fully-locked'

  // ── Concluded-tournament picks display ─────────────────────────────
  // When DataGolf rotates focus off a finished event, pairingsByRound
  // [round] collapses to [] and currentWindow.state degrades to
  // 'not-published' — the same sentinel pre-tournament rounds use.
  // Without disambiguation the existing "haven't been published yet"
  // empty-state hides graded picks the user actually made.
  //
  // Disambiguator: stored picks. Pre-tournament rounds have none; a
  // concluded round retains the picks the user made while it was live.
  //
  // When concluded, synthesize a pairings-shaped array from the picks
  // and feed it to the SAME results-branch render the live fully-locked
  // path uses. pick.groupingMembers was written verbatim from the live
  // pairing's .golfers at pick-time (handlePick + saveGolfPicks), so
  // the shape matches exactly. The only live-pairing field not on the
  // pick is teeTime; the card header below suppresses the 🕐 chip and
  // the tee-time subline when teeTime is null so concluded cards don't
  // show an empty clock.
  //
  // The synthesized list is sorted by groupIdGolf for a deterministic
  // card order across renders (Object.values preserves insertion order
  // in practice but the contract isn't guaranteed across JS engines).
  const isConcluded =
    currentWindow.state === 'not-published'
    && Object.keys(currentPicks).length > 0

  const synthesizedPairings = isConcluded
    ? Object.values(currentPicks)
        .slice()
        .sort((a, b) => String(a.groupIdGolf).localeCompare(String(b.groupIdGolf)))
        .map(pick => ({
          groupId: pick.groupIdGolf,
          round:   selectedRound,
          teeTime: null,
          golfers: Array.isArray(pick.groupingMembers) ? pick.groupingMembers : [],
        }))
    : null

  // Effective values — when NOT concluded these equal the originals
  // EXACTLY, so the live path (open / partial-locked / fully-locked
  // with real pairings) is byte-identical in behavior.
  const effectivePairings      = isConcluded ? synthesizedPairings : currentPairings
  const effectiveIsFullyLocked = isConcluded ? true                : isFullyLocked
  const effectiveTotalPairings = effectivePairings.length

  return (
    // Pinned bg implemented as a separate `position: fixed` layer behind
    // a positioned content wrapper — NOT via `background-attachment: fixed`
    // on the outer div. The element-bg approach worked structurally but
    // showed a documented iOS Safari low-quality scaling pass that
    // visually obscured content (the fixed bg image was rendered at low
    // res, dominating the viewport). A real layered structure with
    // explicit z-indexes ensures bg paints behind content on every
    // browser regardless of attachment quirks.
    //
    // Stacking decision: outer div takes `position: relative` WITHOUT a
    // z-index, so it does NOT create a new stacking context. The fixed
    // bg-layer (z:0), the content wrapper (z:1), the sticky-submit
    // (z:90), and the toast (z:100) all stay in the root stacking
    // context, preserving their existing paint order vs page-header
    // (sticky, z:20), bottom-nav (fixed, z:50), bet-slip-fab (fixed,
    // z:40), modal-backdrop (fixed, z:100), modal screens (fixed, z:200).
    //
    // Negative margins escape parent gutter for full-bleed; padding
    // restores content inset; minHeight ensures coverage with few cards.
    // Asset is picker-only — homepage GroupCard's /sport-bg/golf.webp
    // is untouched.
    <div style={{
      position: 'relative',
      margin: '-16px -16px 0',
      padding: '16px',
      minHeight: 'calc(100vh - 160px)',
    }}>
      {/* Fixed bg layer — pinned to viewport, sits at z:0 in root
          stacking context (parent has no stacking context, so this
          z-index is interpreted against root, below every overlay UI
          element). pointer-events:none so taps pass through to content.
          aria-hidden because decorative. */}
      <div aria-hidden="true" style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background: "linear-gradient(180deg, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.62) 100%), url('/sport-bg/golf-picker.webp') center center / cover no-repeat",
      }} />
      {/* In-flow content wrapper — elevated to z:1 so it paints above
          the bg layer. Wraps only the in-flow picker UI (round selector
          + pairings list). The fixed sticky-submit and toast stay as
          unwrapped siblings of this wrapper so their own z-indexes
          (90 / 100) stay interpreted against root stacking context, not
          clamped into this wrapper's z:1 stacking context (which would
          drop them below bottom-nav at z:50 and hide them). */}
      <div style={{ position: 'relative', zIndex: 1 }}>
      {/* Round selector — one tab per pool round. Badge per tab shows
          the four-window state, with pick count overlay so the user
          sees at-a-glance which rounds are picked / open / locked.
          Glass treatment: the strip itself sits in a backdrop-blur
          container (single element — cheap), individual chips stay
          solid for legibility. */}
      <div style={{
        display:'flex', gap:'6px', overflowX:'auto', marginBottom:'12px',
        paddingBottom:'4px', scrollbarWidth:'thin',
        background:'rgba(8,14,22,0.45)',
        backdropFilter:'blur(14px)',
        WebkitBackdropFilter:'blur(14px)',
        border:'1px solid rgba(255,255,255,0.10)',
        borderRadius:'14px',
        padding:'8px',
      }}>
        {poolRounds.map(r => {
          const w        = roundWindows.find(x => x.round === r) || { state: 'not-published', totalPairings: 0 }
          const active   = r === selectedRound
          // Per-round concluded disambiguator — same logic as the body's
          // isConcluded (L486-488, applied to the selected round only),
          // here applied per-r so every chip can recognize a concluded
          // round and stop showing "Pairings TBA". Truth table:
          //   pairings non-empty  → live truth wins (chip unchanged)
          //   pairings empty AND picks[r] non-empty → concluded (override)
          //   pairings empty AND picks[r] empty → genuinely TBD
          //     (pre-tournament / pre-cut R3-R4 / concluded-but-skipped —
          //     indistinguishable; chip stays "Pairings TBA" as today)
          const isConcludedForR =
            (pairingsByRound[r] || []).length === 0
            && Object.keys(picks[r] || {}).length > 0
          // wEffective drives the chip's COLOR via STATE_COLOR. Concluded
          // rounds borrow the fully-locked treatment (muted color) so they
          // visually read as "round closed" alongside genuinely fully-
          // locked live rounds. wEffective === w when not concluded → live
          // path byte-identical.
          const wEffective = isConcludedForR ? { ...w, state: 'fully-locked' } : w
          const color    = STATE_COLOR[wEffective.state]
          const roundPicked    = picks[r] ? Object.keys(picks[r]).length : 0
          const roundSubmitted = !!submitted[r]
          const showPickCount  = w.totalPairings > 0 && (w.state === 'open' || w.state === 'partial-locked')
          return (
            <button
              key={r}
              type="button"
              onClick={() => setSelectedRound(r)}
              style={{
                flexShrink: 0,
                background: active ? 'var(--gold-bg2)' : color.bg,
                border:`2px solid ${active ? 'var(--gold)' : color.border}`,
                borderRadius:'10px', padding:'8px 14px', cursor:'pointer',
                fontFamily:'inherit', fontSize:'13px',
                color: active ? 'var(--gold)' : color.fg,
                fontWeight: active ? 'bold' : 'normal',
                minWidth: '110px', textAlign: 'left', lineHeight: 1.3,
              }}>
              <div style={{ fontWeight:'bold' }}>
                R{r}
                {roundSubmitted && ' ✓'}
                {/* 🔒 keyed off RAW w.state — semantically "round had a
                    live fully-locked transition we observed". Concluded
                    rounds (raw state still 'not-published') intentionally
                    don't show the lock glyph; their subtitle says
                    'Final' instead. */}
                {w.state === 'fully-locked' && ' 🔒'}
              </div>
              <div style={{
                fontSize:'10px', fontFamily:'Arial,sans-serif',
                color: active ? 'var(--gold)' : color.fg,
                opacity: 0.85,
              }}>
                {/* Subtitle: 'Final' inline for concluded rounds (do NOT
                    add to STATE_LABEL — tests / other readers may key
                    on its current shape, and a 5th state has no
                    semantic delta from fully-locked for chip-render).
                    Live path reads STATE_LABEL[wEffective.state] which
                    === STATE_LABEL[w.state] when wEffective === w. */}
                {isConcludedForR ? 'Final' : STATE_LABEL[wEffective.state]}
                {showPickCount && ` · ${roundPicked}/${w.totalPairings}`}
              </div>
            </button>
          )
        })}
      </div>

      {/* No-pairings state: round hasn't been published yet. Common for
          R3/R4 pre-cut, or for a round that simply isn't in the feed.
          isConcluded short-circuits this — a finished tournament's
          empty pairings + stored picks routes through the synthesized
          path below, not this pre-tournament copy. */}
      {currentWindow.state === 'not-published' && !isConcluded && (
        <div style={{ padding:'40px 20px', textAlign:'center', color:'var(--text-dim)' }}>
          <div style={{ fontSize:'40px', marginBottom:'12px' }}>⏳</div>
          <div style={{ fontSize:'14px', marginBottom:'6px' }}>
            Round {selectedRound} pairings haven't been published yet.
          </div>
          <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'Arial,sans-serif' }}>
            For tournaments with a cut, R3 and R4 pairings appear after R2 finishes.
          </div>
        </div>
      )}

      {currentWindow.state !== 'not-published' && totalPairings === 0 && (
        <div style={{ padding:'40px 20px', textAlign:'center', color:'var(--text-dim)' }}>
          <div style={{ fontSize:'14px' }}>No pairings to render for round {selectedRound}.</div>
        </div>
      )}

      {(currentWindow.state !== 'not-published' || isConcluded) && effectiveTotalPairings > 0 && (
        <>
          {/* Progress + submit row. Glass-strip treatment so it reads
              against the photo bg; same blur as the chip bar above. */}
          <div style={{
            display:'flex', justifyContent:'space-between', alignItems:'center',
            marginBottom:'12px', fontSize:'12px', fontFamily:'Arial,sans-serif',
            background:'rgba(8,14,22,0.45)',
            backdropFilter:'blur(14px)',
            WebkitBackdropFilter:'blur(14px)',
            border:'1px solid rgba(255,255,255,0.10)',
            borderRadius:'12px',
            padding:'8px 12px',
          }}>
            {effectiveIsFullyLocked ? (
              <span style={{ color:'var(--text-dim)' }}>
                Round {selectedRound}: <strong style={{ color:'var(--text)' }}>{correctCount} of {effectiveTotalPairings} correct</strong>
                {' · '}
                <strong style={{ color: pointsTotal > 0 ? 'var(--green)' : 'var(--text)' }}>{pointsTotal} pt{pointsTotal === 1 ? '' : 's'}</strong>
                {' · 🔒 round closed'}
              </span>
            ) : (
              <span style={{ color: pickedCount === effectiveTotalPairings ? 'var(--green)' : 'var(--text-dim)' }}>
                {pickedCount}/{effectiveTotalPairings} picked
                {currentWindow.state === 'partial-locked' && ` · ${currentWindow.lockedPairings} locked`}
                {isSubmitted && ' · ✓ submitted'}
              </span>
            )}
            {/* Submit button moved to a sticky position at the bottom of
                the viewport — see block before the toast. Top row now
                shows only the pick-progress counter; with 36+ pairings
                in a typical Tour event, requiring scroll-to-top to
                submit was friction. */}
          </div>

          {/* Pairings list — per-pairing rendering. Lock state via
              isPairingLocked (shared with the grader's assumptions
              through the same pickWindow module). When concluded
              (synthesized pairings), g.teeTime is null and the
              tee-time chrome is suppressed; the card body renders
              from pick fields the same way it does on a live
              fully-locked round. */}
          {effectivePairings.map((g, idx) => {
            const pick           = currentPicks[g.groupId]
            const golfers        = Array.isArray(g.golfers) ? g.golfers : []
            const pairingLocked  = isPairingLocked(g.teeTime, now, tzOffsetHours)
            // Read-only ONLY when round is fully-locked OR this specific
            // pairing's tee time has passed. Submit does NOT freeze
            // still-open pairings — auto-save is the data path; submit
            // is just the "✓ done" affirmation badge.
            const readOnly       = effectiveIsFullyLocked || pairingLocked
            const formatLabel    = `${golfers.length}-ball`
            const teeLabel       = formatTeeTime(g.teeTime)
            const hasTeeTime     = g.teeTime != null
            return (
              <div key={g.groupId} style={{
                // Glass card — solid rgba (NO per-card backdrop-filter:
                // 49 RBC cards × blur(14px) would chug on mid-range
                // mobile, especially Android). Blur is reserved for the
                // single sticky round-chip bar above. The 0.72 alpha is
                // tuned for high text contrast over the photo while
                // still letting the green hint through between cards.
                background:'rgba(8,14,22,0.72)',
                border:'1px solid rgba(255,255,255,0.10)',
                borderRadius:'16px', marginBottom:'10px', padding:'12px 14px',
                opacity: pairingLocked && !effectiveIsFullyLocked ? 0.75 : 1,
              }}>
                {/* Two-line header per mock — top row is the group label
                    + bold tee time; subline below repeats tee time with
                    the lock countdown (or 🔒 Locked when past).
                    hasTeeTime guards both elements so concluded-card
                    synthesized pairings (teeTime: null) don't render an
                    empty 🕐 chip or a "Tee Time " sub-line with no time. */}
                <div style={{ marginBottom: '10px', fontFamily:'Arial,sans-serif' }}>
                  <div style={{
                    display:'flex', justifyContent:'space-between', alignItems:'center',
                    gap: 8,
                  }}>
                    <span style={{
                      fontSize:'12px', fontWeight: 800, letterSpacing: 0.5,
                      textTransform: 'uppercase', color:'var(--text)',
                    }}>
                      🚩 Group {idx + 1} · {formatLabel.toUpperCase()}
                    </span>
                    {hasTeeTime && (
                      <span style={{
                        fontSize:'12px', fontWeight: 700, color:'var(--text)',
                        display:'inline-flex', alignItems:'center', gap: 4,
                      }}>
                        🕐 {teeLabel}
                      </span>
                    )}
                  </div>
                  {/* Subline — display-only countdown / locked state.
                      Derived from the same parseTeeTimeMs that drives
                      the lock check, so display can never drift. */}
                  {hasTeeTime && (
                    <div style={{
                      fontSize: '10px', color:'var(--text-muted)',
                      marginTop: 3,
                      display:'flex', alignItems:'center', gap: 6,
                    }}>
                      <span>Tee Time {teeLabel}</span>
                      {pairingLocked
                        ? !effectiveIsFullyLocked && (
                          <>
                            <span style={{ opacity: 0.5 }}>·</span>
                            <span style={{ color:'var(--gold)', fontWeight: 700 }}>🔒 Locked</span>
                          </>
                        )
                        : (() => {
                            const cd = formatLockCountdown(g.teeTime, now, tzOffsetHours)
                            return cd && (
                              <>
                                <span style={{ opacity: 0.5 }}>·</span>
                                <span style={{ color:'var(--green)', fontWeight: 700 }}>🔒 {cd}</span>
                              </>
                            )
                          })()
                      }
                    </div>
                  )}
                </div>
                {effectiveIsFullyLocked ? (
                  // Graded-results render: picked golfer gets the ✓/✗/
                  // pending indicator + score + points; remaining members
                  // render with score columns. When the pick LOST, the
                  // winning member's row gets a subtle green tint so the
                  // user can see who beat them (migration 033 surface).
                  // When the round is in-progress (locked but not graded),
                  // live scores from the 75s poll fill the score columns
                  // as "-3 thru 14" provisional treatment.
                  (() => {
                    // Finals (from grader). null on pre-feature OR in-progress.
                    const finals = pick?.roundScores || null
                    const hasFinals = finals != null
                    // Per-pairing leakage gate — fully-locked means
                    // pairingLocked is always true here, so the gate reduces
                    // to isInProgress && !hasFinals. Keep the explicit call
                    // so the rule stays grep-able from one place.
                    const showLive = shouldShowLiveScores({
                      isInProgress,
                      pairingLocked: true,
                      hasFinals,
                    })
                    // Per-golfer cell resolver. Order of precedence:
                    //   1. Finals (when graded) — bold, tinted, no thru
                    //   2. Live (when in-progress + showLive) — muted, italic, with "thru N"
                    //   3. Empty — "—"
                    function cellFor(dgId) {
                      if (hasFinals && finals[dgId] !== undefined) {
                        return { kind: 'final', text: formatScoreToPar(finals[dgId]), thru: null }
                      }
                      const live = showLive ? liveScoresByDgId[dgId] : null
                      if (live && live.status === 'active' && live.score != null) {
                        return {
                          kind: 'live',
                          text: formatScoreToPar(live.score),
                          thru: live.thru != null ? `thru ${live.thru}` : null,
                        }
                      }
                      return { kind: 'empty', text: '—', thru: null }
                    }
                    // Winner highlight: only on FINAL losing picks. During
                    // in-progress we deliberately do NOT compute a
                    // provisional winner — picks are pending, the app
                    // asserts nothing until the gate grades.
                    let winnerDgId = null
                    if (hasFinals && pick && pick.result === 'incorrect') {
                      let lowScore = Infinity
                      for (const g of golfers) {
                        if (g.dgId === pick.selectedDgId) continue
                        const s = finals[g.dgId]
                        if (typeof s === 'number' && s < lowScore) {
                          lowScore = s
                          winnerDgId = g.dgId
                        }
                      }
                    }
                    return (
                      <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
                        {pick ? (
                          (() => {
                            const result      = pick.result || 'pending'
                            const isCorrect   = result === 'correct'
                            const isIncorrect = result === 'incorrect'
                            const indicator   = isCorrect ? '✓' : isIncorrect ? '✗' : '·'
                            const tone        = isCorrect ? 'var(--green)'
                                              : isIncorrect ? 'var(--red)'
                                              : 'var(--gold)'
                            const cell        = cellFor(pick.selectedDgId)
                            // Live (in-progress) score tone is muted neutral,
                            // NOT green/red — the pick isn't decided.
                            const scoreColor   = cell.kind === 'live'   ? 'var(--text-dim)'
                                               : cell.kind === 'final' ? tone
                                               : 'var(--text-muted)'
                            const scoreItalic  = cell.kind === 'live'
                            return (
                              <div style={{
                                display:'flex', justifyContent:'space-between', alignItems:'center',
                                background:'var(--surface)', border:`2px solid ${tone}`,
                                borderRadius:'8px', padding:'10px 12px',
                                fontFamily:'inherit', fontSize:'14px',
                                gap: 8,
                              }}>
                                <span style={{ color: tone, fontWeight:'bold', flex: 1, minWidth: 0 }}>
                                  {indicator} {pick.selectedName || 'Picked golfer'}
                                </span>
                                <span style={{
                                  color: scoreColor, fontFamily:'Arial,sans-serif',
                                  fontSize:'13px', fontWeight: cell.kind === 'final' ? 'bold' : 'normal',
                                  fontStyle: scoreItalic ? 'italic' : 'normal',
                                  textAlign:'right',
                                  display:'flex', flexDirection:'column', alignItems:'flex-end',
                                  lineHeight: 1.15,
                                }}>
                                  <span>{cell.text}</span>
                                  {cell.thru && (
                                    <span style={{ fontSize:'10px', color:'var(--text-muted)' }}>
                                      {cell.thru}
                                    </span>
                                  )}
                                </span>
                                <span style={{
                                  color: isCorrect ? 'var(--green)' : 'var(--text-muted)',
                                  fontFamily:'Arial,sans-serif', fontSize:'12px', fontWeight:'bold',
                                  minWidth: 44, textAlign:'right',
                                }}>
                                  {isCorrect ? `+${pick.pointsEarned || 1}pt`
                                   : isIncorrect ? '0 pt'
                                   : 'pending'}
                                </span>
                              </div>
                            )
                          })()
                        ) : (
                          <div style={{
                            background:'var(--surface)', border:'2px solid var(--border)',
                            borderRadius:'8px', padding:'10px 12px',
                            color:'var(--text-muted)', fontStyle:'italic',
                            fontFamily:'inherit', fontSize:'14px',
                          }}>
                            — no pick
                          </div>
                        )}
                        {golfers
                          .filter(golfer => golfer.dgId !== pick?.selectedDgId)
                          .map(golfer => {
                            const isWinner = golfer.dgId === winnerDgId
                            const cell = cellFor(golfer.dgId)
                            return (
                              <div key={golfer.dgId} style={{
                                display:'flex', justifyContent:'space-between', alignItems:'center',
                                padding:'6px 12px',
                                color: isWinner ? 'var(--green)' : 'var(--text-muted)',
                                background: isWinner ? 'rgba(0, 230, 118, 0.10)' : 'transparent',
                                borderRadius: isWinner ? '6px' : 0,
                                fontFamily:'inherit', fontSize:'13px',
                                opacity: isWinner ? 0.9 : 0.55,
                                gap: 8,
                              }}>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  {displayName(golfer.name)}
                                </span>
                                <span style={{
                                  fontFamily:'Arial,sans-serif',
                                  fontSize:'12px',
                                  fontWeight: isWinner || cell.kind === 'final' ? 'bold' : 'normal',
                                  fontStyle: cell.kind === 'live' ? 'italic' : 'normal',
                                  textAlign:'right',
                                  display:'flex', flexDirection:'column', alignItems:'flex-end',
                                  lineHeight: 1.15,
                                }}>
                                  <span>{cell.text}</span>
                                  {cell.thru && (
                                    <span style={{ fontSize:'9px', color:'var(--text-muted)' }}>
                                      {cell.thru}
                                    </span>
                                  )}
                                </span>
                              </div>
                            )
                          })
                        }
                      </div>
                    )
                  })()
                ) : (
                  // Picker branch — at least one pairing in the round is still
                  // open. Locked pairings within this round are read-only
                  // (readOnly = pairingLocked). During in-progress, a locked
                  // pairing swaps its R1-R4 score strip for a LIVE indicator
                  // (per-pairing leakage gate: shouldShowLiveScores). Open
                  // pairings keep the R1-R4 strip unchanged — showing live
                  // opponent scores on an open pick window would leak into
                  // the pick decision.
                  (() => {
                    const showLiveThisPairing = shouldShowLiveScores({
                      isInProgress,
                      pairingLocked,
                      hasFinals: false,   // picker branch never has finals (would route to isFullyLocked instead)
                    })
                    return (
                  <div style={{ display:'flex', flexDirection:'column' }}>
                    {golfers.map((golfer, gIdx) => {
                      const selected    = pick?.selectedDgId === golfer.dgId
                      const interactive = !readOnly
                      const flag        = flagUrl(golfer.country)
                      const initials    = playerInitials(golfer.name)
                      const display     = displayName(golfer.name)
                      // golfer.roundScores from /api/golf/pairings is always {} since
                      // 2026-06-10 (the parallel-fan-out timeout fix). R1-R4 strip
                      // now reads pickScoresByRoundByDgId + roundScoresAll instead;
                      // see the strip render below for the precedence logic.
                      const live = showLiveThisPairing ? liveScoresByDgId[golfer.dgId] : null
                      const liveActive = live && live.status === 'active' && live.score != null
                      return (
                        <button
                          key={golfer.dgId}
                          type="button"
                          disabled={!interactive}
                          onClick={() => interactive && handlePick(g, golfer)}
                          style={{
                            // Selected row — green tint per mock. Avatar
                            // ring stays gold-on-select for hierarchy
                            // (gold ring + green text = the mock's
                            // "winner pick" treatment).
                            background: selected ? 'rgba(0, 230, 118, 0.14)' : 'transparent',
                            border: 'none',
                            borderTop: gIdx === 0 ? 'none' : '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: 12,
                            padding:'10px 8px',
                            cursor: interactive ? 'pointer' : 'default',
                            textAlign:'left', fontFamily:'inherit', fontSize:'14px',
                            color: selected ? 'var(--green)' : 'var(--text)',
                            opacity: interactive || selected ? 1 : 0.7,
                            display:'flex', alignItems:'center', gap:'10px',
                            width:'100%',
                          }}>
                          {/* Initials avatar — green ring when interactive, gold when selected */}
                          <div style={{
                            flexShrink: 0,
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'var(--surface)',
                            border: `2px solid ${selected ? 'var(--gold)' : 'var(--green)'}`,
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize: 12, fontWeight: 700,
                            color: selected ? 'var(--gold)' : 'var(--text)',
                            fontFamily:'inherit',
                          }}>{initials}</div>

                          {/* Flag chip — falls back to a small alpha-3 label if no slug match */}
                          {flag ? (
                            <img src={flag} alt={golfer.country || ''} width="24" height="18"
                                 style={{ flexShrink: 0, borderRadius: 2, objectFit:'cover' }}
                                 loading="lazy"
                                 onError={e => { e.currentTarget.style.display = 'none' }} />
                          ) : golfer.country ? (
                            <span style={{
                              flexShrink: 0, fontSize: 9, fontWeight: 700,
                              padding: '2px 4px', borderRadius: 3,
                              background:'var(--surface)', color:'var(--text-muted)',
                              fontFamily:'Arial,sans-serif',
                            }}>{golfer.country}</span>
                          ) : (
                            <span style={{ flexShrink: 0, width: 24 }} />
                          )}

                          {/* Name + prior-round score strip */}
                          <div style={{ flex: 1, minWidth: 0, display:'flex', flexDirection:'column', gap: 2 }}>
                            <span style={{
                              fontWeight: 700, fontSize: 14,
                              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                            }}>
                              {selected ? '✓ ' : ''}{display}
                            </span>
                            {/* Score strip — two modes:
                                  (a) LIVE indicator when this pairing is locked AND
                                      the round is in progress (provisional, muted,
                                      italic, with "thru N"). Per-pairing leakage gate.
                                  (b) Otherwise: R1-R4 strip — per-round per-golfer
                                      with grader-snapshot precedence and live fall-
                                      back for non-picked golfers + in-progress
                                      rounds. See inline notes at the strip render. */}
                            {live ? (
                              <span style={{
                                fontSize: 11, fontFamily:'Arial,sans-serif',
                                color:'var(--text-muted)', fontStyle:'italic',
                              }}>
                                LIVE{' '}
                                <strong style={{ color:'var(--text-dim)' }}>
                                  {liveActive ? formatScoreToPar(live.score) : '—'}
                                </strong>
                                {liveActive && live.thru != null && (
                                  <span style={{ marginLeft: 6, color:'var(--text-muted)' }}>
                                    thru {live.thru}
                                  </span>
                                )}
                              </span>
                            ) : (
                              // R1-R4 strip — per-round, per-golfer.
                              //
                              // Source precedence (Part 2 YES branch):
                              //   1. pickScoresByRoundByDgId[r][dgId] — grader
                              //      snapshot, immutable post-finalize. Used
                              //      whenever the golfer was in the user's
                              //      pairing for round r. Authoritative for
                              //      the user's grading reality even if
                              //      DataGolf later revises the score.
                              //   2. roundScoresAll.scoresByDg[dgId][r] —
                              //      live DataGolf via the new round-scores
                              //      endpoint. Styled differently by round
                              //      state:
                              //        rounds[r]='final' → authoritative
                              //        rounds[r]='in_progress' → live-styled
                              //                 (italic + muted color) so a
                              //                 provisional number never
                              //                 reads as a graded final
                              //        rounds[r]='no_data' or missing → dash
                              //
                              // null score (from either source) on a finished
                              // round → "—" non-score marker (path-b absence
                              // or WD). Never "0" — zero is a real golf score.
                              //
                              // Formatting via formatScoreToPar: E for 0,
                              // "+N" for positives, "-N" for negatives.
                              <span style={{
                                fontSize: 11, fontFamily:'Arial,sans-serif',
                                color:'var(--text-muted)',
                                display:'flex', gap: 8, flexWrap:'wrap',
                              }}>
                                {[1, 2, 3, 4].map(rn => {
                                  const dgIdStr  = String(golfer.dgId)
                                  const snap     = pickScoresByRoundByDgId[rn] || {}
                                  const hasSnap  = Object.prototype.hasOwnProperty.call(snap, dgIdStr)
                                  const liveR    = roundScoresAll.scoresByDg?.[dgIdStr]?.[rn]
                                  const roundSt  = roundScoresAll.rounds?.[rn]
                                  let text       = '—'
                                  let color      = 'var(--text-muted)'
                                  let italic     = false
                                  if (hasSnap) {
                                    // Grader-snapshot precedence — authoritative for finished+picked.
                                    const v = snap[dgIdStr]
                                    text  = v == null ? '—' : formatScoreToPar(v)
                                    color = 'var(--text-dim)'
                                  } else if (roundSt === 'final') {
                                    // Live endpoint says finished, no snapshot available
                                    // (golfer not in user's pairing) → use live as authoritative.
                                    text  = liveR == null ? '—' : formatScoreToPar(liveR)
                                    color = 'var(--text-dim)'
                                  } else if (roundSt === 'in_progress') {
                                    // Provisional — live-styled (italic + muted) so the user can
                                    // see it's not a graded final.
                                    text   = liveR == null ? '—' : formatScoreToPar(liveR)
                                    color  = 'var(--text-muted)'
                                    italic = true
                                  }
                                  // else: rounds[rn] is 'no_data' / undefined → dash treatment.
                                  return (
                                    <span key={rn}>
                                      R{rn} <strong style={{ color, fontStyle: italic ? 'italic' : 'normal' }}>
                                        {text}
                                      </strong>
                                    </span>
                                  )
                                })}
                              </span>
                            )}
                          </div>

                          {/* Right-side: World Rank label + value stacked */}
                          <div style={{
                            flexShrink: 0, textAlign:'right', lineHeight: 1.1,
                            fontFamily:'Arial,sans-serif',
                          }}>
                            <div style={{ fontSize: 9, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing: 0.5 }}>
                              World Rank
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700, color:'var(--text)' }}>
                              {golfer.worldRank ?? '—'}
                            </div>
                          </div>

                          {/* Circular pick affordance — "+" when pickable,
                              "✓" when chosen. Both states stay green per
                              mock; selected just swaps "+" → "✓". */}
                          <div style={{
                            flexShrink: 0,
                            width: 28, height: 28, borderRadius: '50%',
                            background: interactive || selected ? 'var(--green)' : 'var(--surface)',
                            color: interactive || selected ? 'var(--surface)' : 'var(--text-muted)',
                            display:'flex', alignItems:'center', justifyContent:'center',
                            fontSize: 16, fontWeight: 700,
                            fontFamily:'inherit',
                          }}>
                            {selected ? '✓' : '+'}
                          </div>
                        </button>
                      )
                    })}
                    {/* Card footer strip per mock — "N PLAYERS · PICK 1
                        WINNER". VIEW COURSE intentionally omitted per
                        operator spec. Only renders on pickable cards
                        (not graded). */}
                    {!effectiveIsFullyLocked && (
                      <div style={{
                        marginTop: 8,
                        padding: '8px 10px',
                        borderTop: '1px solid rgba(255,255,255,0.08)',
                        display:'flex', justifyContent:'space-between', alignItems:'center',
                        fontSize: 10, fontFamily:'Arial,sans-serif',
                        color:'var(--text-muted)',
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                      }}>
                        <span style={{ color:'var(--green)', fontWeight: 700 }}>
                          👥 {golfers.length} Players
                        </span>
                        <span style={{ fontWeight: 700 }}>
                          Pick 1 Winner
                        </span>
                      </div>
                    )}
                  </div>
                    )
                  })()
                )}
              </div>
            )
          })}
        </>
      )}
      </div>{/* end content wrapper (z:1) — sticky-submit + toast below stay siblings of bg layer, NOT inside the wrapper */}

      {/* Sticky Submit — anchored above the bottom nav so it's reachable
          from any scroll position in a long pairings list (e.g. Memorial's
          36 pairings in 2-balls). Positioned at bottom:80 to clear the
          .bottom-nav (~55px + safe-area-inset-bottom); the toast below
          is bumped to bottom:140 so the two stack cleanly if both render
          at once (the failed-submit retry case where canSubmit flips
          back on while the error toast is up). On success the button
          disappears before the toast shows, so the common case has no
          overlap. canSubmit gating is identical to the old inline
          button — submit-doesn't-freeze behavior (commit 2410db4) is
          preserved verbatim. */}
      {/* Sticky-submit — suppressed when `locked` so the sticky unlock
          bar (below) is the SOLE bottom CTA at z:90. Toast at z:100
          stays above either element. */}
      {!locked && canSubmit && (
        <button
          type="button"
          onClick={handleSubmit}
          className="btn btn-gold"
          style={{
            position: 'fixed',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto',
            minWidth: 220,
            padding: '12px 28px',
            fontSize: 14,
            fontWeight: 700,
            zIndex: 90,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            fontFamily: 'inherit',
          }}
        >
          Submit Round {selectedRound}
        </button>
      )}

      {/* Pattern #2 sticky unlock bar — mirrors GolfDivisionsMode 66f2a6b,
          GroupChat 52dd228, PickemMode 4b3e78b exactly. Fixed at
          bottom:80/z:90 (same slot the sticky-submit occupies when
          unlocked — they never both render). Toast at z:100 stays
          above. */}
      {locked && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            left: 16,
            right: 16,
            maxWidth: 500,
            margin: '0 auto',
            background: 'var(--surface2)',
            border: '1px solid var(--gold)',
            borderRadius: 12,
            padding: '12px 14px',
            zIndex: 90,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
              🔒 Pool is locked
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-dim)', marginTop: 2,
              fontFamily: 'Arial, sans-serif',
            }}>
              Unlock to make picks
            </div>
          </div>
          <button
            type="button"
            onClick={onUnlockRequest}
            disabled={unlocking}
            className="btn btn-gold"
            style={{
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 700,
              opacity: unlocking ? 0.6 : 1,
              cursor: unlocking ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {unlocking
              ? 'Unlocking…'
              : (unlockPriceCents != null
                  ? `Unlock $${(unlockPriceCents / 100).toFixed(2)}`
                  : 'Unlock')}
          </button>
        </div>
      )}

      {toast && (
        <div style={{
          position:'fixed', bottom:'140px', left:'50%', transform:'translateX(-50%)',
          background:'var(--surface2)', border:'1px solid var(--border)',
          borderRadius:'8px', padding:'10px 16px', fontSize:'12px', color:'var(--text)',
          zIndex:100, boxShadow:'0 4px 12px rgba(0,0,0,0.3)',
          fontFamily:'inherit',
        }}>
          {toast}
        </div>
      )}
    </div>
  )
}
